"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronDown, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      setScrollY(-rect.top);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.7;
    }
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let animationId: number;
    let phase = 0;
    const waitForCanvasReady = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const width = parent.clientWidth;
      const height = parent.clientHeight;
      if (width === 0 || height === 0) {
        animationId = requestAnimationFrame(waitForCanvasReady);
        return;
      }
      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      startAnimation(ctx, width, height);
    };
    const startAnimation = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
      const draw = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.strokeStyle = "hsla(200, 95%, 45%, 0.12)";
        ctx.lineWidth = 1;
        for (let i = 0; i <= 4; i++) {
          const y = height / 4 * i;
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(width, y);
          ctx.stroke();
        }
        for (let i = 0; i <= 8; i++) {
          const x = width / 8 * i;
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, height);
          ctx.stroke();
        }
        ctx.strokeStyle = "hsl(200, 95%, 45%)";
        ctx.lineWidth = 1.5;
        ctx.shadowColor = "hsl(200, 95%, 45%)";
        ctx.shadowBlur = 8;
        ctx.beginPath();
        for (let x = 0; x < width; x++) {
          const t = x / width * Math.PI * 4 + phase;
          const y = height / 2 + Math.sin(t) * (height * 0.25) + Math.sin(t * 2.3) * (height * 0.1) + Math.sin(t * 5.7) * (height * 0.05);
          if (x === 0) ctx.moveTo(x, y);else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
        phase += 0.04;
        animationId = requestAnimationFrame(draw);
      };
      draw();
    };
    waitForCanvasReady();
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, []);
  return <section data-section-id="4626" ref={sectionRef} id="rewolucja-w-ochronie-dachu" className="dark bg-background text-foreground relative w-full min-h-[720px] h-screen overflow-hidden font-default">
      {}
      <div className="absolute inset-0 w-full h-[120%] -top-[10%]" style={{
      transform: `translate3d(0, ${scrollY * 0.3}px, 0)`
    }}>
        <video ref={videoRef} autoPlay loop muted playsInline poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1726265/img/hydro_roof_spray_process.png" className="w-full h-full object-cover">
          <source src="/img/hydro_roof_spray_video.mp4" type="video/mp4" />
        </video>
      </div>

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse at center, hsla(222, 24%, 8%, 0.4) 0%, hsla(222, 24%, 8%, 0.7) 60%, hsla(222, 24%, 8%, 0.85) 100%)"
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none opacity-20 mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {}
      <div className="absolute top-8 right-8 md:top-12 md:right-12 z-10 flex items-start gap-8 font-mono text-[10px] tracking-widest text-primary/70">
        <div className="flex flex-col items-end gap-2">
          <Plus className="w-3 h-3" strokeWidth={1} />
          <span>HR: 01.TECH</span>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Plus className="w-3 h-3" strokeWidth={1} />
          <span>ISO: 9001</span>
        </div>
      </div>

      {}
      <div className="absolute top-8 left-8 md:top-12 md:left-12 z-10 flex items-center gap-3 font-mono text-[10px] tracking-widest text-foreground/50">
        <span className="inline-block w-2 h-2 bg-primary animate-pulse" />
        <span>HYDRO_ROOF // LIVE_SYSTEM</span>
      </div>

      {}
      <div className="relative z-10 h-full flex flex-col justify-end px-6 sm:px-10 md:px-16 lg:px-24 pb-24 md:pb-28 lg:pb-32">
        <div className="max-w-5xl">
          {}
          <div className="mb-8 md:mb-10 font-mono text-[11px] md:text-xs tracking-[0.2em] text-primary uppercase">
            // HYDRO_ROOF &mdash; PROFESJONALNA HYDROIZOLACJA &mdash; ŚLĄSK
          </div>

          {}
          <h1 className="font-default font-medium text-foreground mb-6 md:mb-8 leading-[0.95]" style={{
          fontSize: "clamp(2.5rem, 8vw, 7rem)",
          letterSpacing: "-0.03em"
        }}></h1>

          {}
          <p className="text-base md:text-lg text-muted-foreground max-w-xl mb-10 md:mb-12 leading-relaxed">
            Profesjonalna renowacja i uszczelnianie dachów metodą natryskową. Skuteczna bariera dla wody, UV i warunków atmosferycznych.
          </p>

          {}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <Button asChild size="lg" className="group bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-7 h-12 font-mono text-xs tracking-[0.15em] uppercase shadow-lg">
              <Link to="/ContactPage">
                Bezpłatna wycena
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>

            <Button asChild variant="ghost" size="lg" className="group relative px-2 h-12 font-mono text-xs tracking-[0.15em] uppercase text-foreground hover:bg-transparent hover:text-primary">
              <Link to="/ProjectsPage">
                <span className="relative">
                  Nasze realizacje
                  <span className="absolute -bottom-1 left-0 w-full h-px bg-current origin-left scale-x-100 transition-transform duration-300 group-hover:scale-x-0" />
                  <span className="absolute -bottom-1 left-0 w-full h-px bg-primary origin-right scale-x-0 transition-transform duration-300 group-hover:scale-x-100 group-hover:origin-left" />
                </span>
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-8 right-8 md:bottom-12 md:right-12 z-10 hidden sm:block">
        <div className="mb-2 flex items-center justify-between gap-4 font-mono text-[10px] tracking-widest text-primary/70 uppercase">
          <span>// SYSTEM_STATUS</span>
          <span>ACTIVE</span>
        </div>
        <div className="relative w-[200px] h-[80px] border border-primary/30 bg-background/40 backdrop-blur-sm">
          <canvas ref={canvasRef} className="absolute inset-0" />
          <div className="absolute top-1 left-1 font-mono text-[8px] text-primary/60">
            FLOW
          </div>
          <div className="absolute bottom-1 right-1 font-mono text-[8px] text-primary/60">
            BAR/DIV
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-8 left-8 md:bottom-12 md:left-16 lg:left-24 z-10 flex flex-col items-start gap-3 animate-pulse">
        <span className="font-mono text-[10px] tracking-[0.3em] text-foreground/60 uppercase">
          Przewiń
        </span>
        <ChevronDown className="w-4 h-4 text-foreground/60" strokeWidth={1.5} />
      </div>

      {}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden lg:flex flex-col items-center gap-3">
        <div className="w-px h-24 bg-gradient-to-b from-transparent via-primary/40 to-transparent" />
        <span className="font-mono text-[10px] tracking-[0.3em] text-primary/60 uppercase" style={{
        writingMode: "vertical-rl"
      }}>
          v.2024.HR
        </span>
        <div className="w-px h-24 bg-gradient-to-b from-transparent via-primary/40 to-transparent" />
      </div>
    </section>;
}